import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DezzyAgent, AgentOperation, AgentResponse, createAgentContext } from '../agent';
import { useAgentStore } from '../state/agent-store';
import { ChatMessage } from '../ui/ChatInterface';

export interface UseAgentOperationsOptions {
  onSuccess?: (response: AgentResponse) => void;
  onError?: (error: Error) => void;
}

export function useAgentOperations(options: UseAgentOperationsOptions = {}) {
  const queryClient = useQueryClient();
  const { 
    currentProject, 
    addMessage, 
    setProcessing, 
    recordOperation,
    setSelectedStory 
  } = useAgentStore();

  // Create agent instance
  const createAgent = () => {
    const context = createAgentContext(currentProject);
    return new DezzyAgent(context);
  };

  // Main operation mutation
  const operationMutation = useMutation({
    mutationFn: async (operation: AgentOperation): Promise<AgentResponse> => {
      setProcessing(true);
      
      const agent = createAgent();
      const response = await agent.processOperation(operation);
      
      // Record operation for tracking
      recordOperation({
        type: operation.type,
        target: operation.target,
        timestamp: new Date(),
        success: response.success
      });
      
      return response;
    },
    onSuccess: (response, operation) => {
      // Add agent response message
      const message: Omit<ChatMessage, 'id' | 'timestamp'> = {
        type: 'agent',
        content: response.success 
          ? `✅ Successfully processed: ${operation.type}\n\n${response.suggestions?.join('\n') || 'Operation completed successfully'}`
          : `❌ Operation failed: ${response.errors?.join('\n') || 'Unknown error'}\n\n${response.suggestions?.join('\n') || ''}`,
        metadata: {
          operation: operation.type,
          success: response.success,
          dslChanges: response.dslChanges
        }
      };
      
      addMessage(message);
      
      // Update selected story if successful and involves UI changes
      if (response.success && response.data?.variant) {
        const storyId = `${operation.target}--${response.data.variant.name}`;
        setSelectedStory(storyId);
      }
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['project-sections'] });
      queryClient.invalidateQueries({ queryKey: ['agent-context'] });
      
      options.onSuccess?.(response);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addMessage({
        type: 'agent',
        content: `❌ Operation failed: ${errorMessage}`
      });
      
      options.onError?.(error instanceof Error ? error : new Error(String(error)));
    },
    onSettled: () => {
      setProcessing(false);
    }
  });

  // Helper functions for common operations
  const addVariant = (sectionName: string, variantDetails: {
    name: string;
    description?: string;
    props?: Record<string, any>;
    tokens?: Record<string, string>;
  }) => {
    const operation: AgentOperation = {
      type: 'ADD_VARIANT',
      target: sectionName,
      parameters: {
        variantName: variantDetails.name,
        description: variantDetails.description,
        props: variantDetails.props || {},
        tokens: variantDetails.tokens || {}
      },
      context: createAgentContext(currentProject)
    };
    
    return operationMutation.mutateAsync(operation);
  };

  const createSection = (sectionName: string, sectionDetails: {
    category: 'element' | 'component' | 'section';
    description?: string;
    props?: Record<string, any>;
    tokens?: Record<string, string>;
  }) => {
    const operation: AgentOperation = {
      type: 'CREATE_SECTION',
      target: sectionName,
      parameters: sectionDetails,
      context: createAgentContext(currentProject)
    };
    
    return operationMutation.mutateAsync(operation);
  };

  const updateView = (viewId: string, viewUpdates: any) => {
    const operation: AgentOperation = {
      type: 'UPDATE_VIEW',
      target: viewId,
      parameters: viewUpdates,
      context: createAgentContext(currentProject)
    };
    
    return operationMutation.mutateAsync(operation);
  };

  const validateDesign = (target: string = 'current') => {
    const operation: AgentOperation = {
      type: 'VALIDATE_DESIGN',
      target,
      parameters: {},
      context: createAgentContext(currentProject)
    };
    
    return operationMutation.mutateAsync(operation);
  };

  // Process natural language input
  const processUserInput = async (input: string): Promise<AgentResponse> => {
    // Add user message first
    addMessage({
      type: 'user',
      content: input
    });

    // Parse input into operation
    const operation = parseUserInput(input);
    
    if (!operation) {
      const response: AgentResponse = {
        success: false,
        errors: ['Could not understand the request'],
        suggestions: [
          'Try being more specific about what you want to build',
          'Examples: "Add a large variant to Button", "Create a new Card section"'
        ]
      };
      
      addMessage({
        type: 'agent',
        content: `I understand you want to: "${input}"\n\nI can help with:\n• Adding variants to existing sections\n• Creating new sections\n• Updating views\n• Validating designs\n\nTry being more specific, like "Add a large variant to the Button section"`
      });
      
      return response;
    }

    return operationMutation.mutateAsync(operation);
  };

  // Simple intent detection (can be enhanced with NLP)
  const parseUserInput = (input: string): AgentOperation | null => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('add') && lowerInput.includes('variant')) {
      const sectionMatch = input.match(/(?:to|in|for)\s+(\w+)/i);
      const variantMatch = input.match(/add\s+(?:a\s+)?(\w+)\s+variant/i);
      
      return {
        type: 'ADD_VARIANT',
        target: sectionMatch?.[1] || 'Button',
        parameters: {
          variantName: variantMatch?.[1] || 'newVariant',
          props: {},
          tokens: {}
        },
        context: createAgentContext(currentProject)
      };
    }
    
    if (lowerInput.includes('create') || lowerInput.includes('new')) {
      return {
        type: 'CREATE_SECTION',
        target: input.match(/(?:create|new)\s+(\w+)/i)?.[1] || 'NewComponent',
        parameters: {
          category: 'component' as const
        },
        context: createAgentContext(currentProject)
      };
    }
    
    if (lowerInput.includes('validate')) {
      return {
        type: 'VALIDATE_DESIGN',
        target: 'current',
        parameters: {},
        context: createAgentContext(currentProject)
      };
    }
    
    return null;
  };

  return {
    // Core mutation
    processOperation: operationMutation.mutateAsync,
    isProcessing: operationMutation.isPending,
    error: operationMutation.error,
    
    // Helper functions
    addVariant,
    createSection,
    updateView,
    validateDesign,
    processUserInput,
    
    // State
    lastOperation: useAgentStore((state) => state.lastOperation)
  };
}